import { dataLayerPush } from '../common';
import { getShortErrorName } from './errorName';
import { getApiResponseFailureReason } from './response';
import { getApiSource, getCanonicalApiName } from './url';
import { isObject } from './utils';
import { shouldSuppressApiErrorTracking } from './suppression';

export { getShortErrorName } from './errorName';
export { getApiResponseFailureReason } from './response';
export { getApiSource, getCanonicalApiName } from './url';
export { IGNORE_API_LIST, shouldSuppressApiErrorTracking } from './suppression';

type InterceptorError = Error & {
  data?: Response;
  response?: Response;
  requestUrl?: string;
};

type ApiFailureDetails = {
  apiName: string;
  apiSource: string;
  errorName: string;
  reason: string;
  requestUrl: string;
  statusCode: number | string;
};

const trackedErrors = new WeakSet<InterceptorError>();

const pushApiFailure = ({
  apiName,
  apiSource,
  errorName,
  reason,
  requestUrl,
  statusCode
}: ApiFailureDetails): void => {
  if (
    statusCode === 'noStatus' &&
    apiSource === 'unknown' &&
    apiName === 'unknownApi'
  ) {
    return;
  }
  if (shouldSuppressApiErrorTracking(requestUrl)) return;

  const pageUrl =
    typeof window === 'undefined'
      ? 'unknown'
      : `${window.location.host}${window.location.pathname}`;

  dataLayerPush({
    event: 'track-event',
    event_name: 'singtel-error',
    event_category: 'API-error',
    event_action: 'error-prompt',
    event_label: `err_api_${statusCode}_${apiSource}_${apiName}_${reason}`,
    error_name: errorName,
    page_url_without_query_string_parameters: pageUrl
  });
};

export const trackApiResponseFailure = (
  response: Response,
  responseJson: unknown
): void => {
  try {
    const reason = getApiResponseFailureReason(responseJson);
    if (!reason) return;

    const responseBody = isObject(responseJson) ? responseJson : undefined;
    const metadata = isObject(responseBody?.metadata)
      ? responseBody.metadata
      : undefined;
    const requestUrl =
      response?.url ||
      (typeof metadata?.path === 'string' ? metadata.path : '');
    const statusCode = response?.status || 200;

    pushApiFailure({
      apiName: getCanonicalApiName(requestUrl),
      apiSource: getApiSource(requestUrl),
      errorName: getShortErrorName(responseJson, reason, statusCode),
      reason,
      requestUrl,
      statusCode
    });
  } catch {
    // Analytics failures must never change the application flow.
  }
};

export const trackApiFailure = (
  error: InterceptorError,
  responseJson?: unknown
): void => {
  try {
    if (typeof error === 'object' && error !== null) {
      if (trackedErrors.has(error)) return;
      trackedErrors.add(error);
    }

    const response = error?.data || error?.response;
    const requestUrl = response?.url || error?.requestUrl || '';
    const statusCode = response?.status || 'noStatus';

    pushApiFailure({
      apiName: getCanonicalApiName(requestUrl),
      apiSource: getApiSource(requestUrl),
      errorName: getShortErrorName(responseJson, 'serviceError', statusCode),
      reason: 'serviceError',
      requestUrl,
      statusCode
    });
  } catch {
    // Analytics failures must never change the application flow.
  }
};
